import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { PlatformChartCycleMomentsDto } from '@data/_generated-api/platform-api/models/platform-chart-cycle-moments-dto';
import { PlatformChartDto } from '@data/_generated-api/platform-api/models/platform-chart-dto';
import { PlatformChartNoteAttachmentDto } from '@data/_generated-api/platform-api/models/platform-chart-note-attachment-dto';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { RemoteChartChange, setChartChangeListener } from '@common/chart-change-signals/chart-change-signals';
import { HdChartCyclesService } from './hd-chart-cycles.service';
import { HdChartDataEvent, HdChartDataEvents } from './hd-chart-data-events';
import { HdChartGroupService } from './hd-chart-group.service';
import { HdChartLibraryService } from './hd-chart-library.service';
import { HdCacheEntry, HdKeyedCache } from './hd-keyed-cache';

/** Sleutel voor de cycle-momenten: chart + solar-return-jaar (`next` = de eerstvolgende). */
export function cycleMomentsKey(chartId: string, solarReturnYear?: number | null): string {
  return `${chartId}|${solarReturnYear ?? 'next'}`;
}

function parseCycleMomentsKey(key: string): { chartId: string; solarReturnYear: number | undefined } {
  const [chartId, year] = key.split('|');
  const parsed = Number(year);
  return { chartId, solarReturnYear: year === 'next' || !Number.isFinite(parsed) ? undefined : parsed };
}

function isColdEntry(entry: HdCacheEntry<unknown> | undefined): boolean {
  return !!entry && entry.loading && entry.value === undefined;
}

/**
 * Sessiecache voor serverdata per chart (spec chart-datacache 2026-10-06). Panelen tonen hieruit en
 * registreren zich als kijker (`watch`); een tabwissel naar een al bezochte chart doet zo geen calls.
 *
 * Bronnen van wijziging:
 * - eigen mutaties via de chart-services (`HdChartDataEvents`): bijwerken waar het antwoord dat
 *   toelaat, anders als oud markeren;
 * - live seintjes van andere toestellen/browsertabs (`chart-change-signals`): als oud markeren;
 *   wat in beeld is, herlaadt meteen stil;
 * - herverbinding van de live verbinding: alles als oud (seintjes kunnen gemist zijn).
 */
@Injectable({ providedIn: 'root' })
export class HdChartDataCache {
  private readonly groupService = inject(HdChartGroupService);
  private readonly library = inject(HdChartLibraryService);
  private readonly cyclesService = inject(HdChartCyclesService);

  readonly relations = new HdKeyedCache<PlatformChartRelationDto[]>(chartId => this.groupService.listRelations(chartId));
  readonly noteAttachments = new HdKeyedCache<PlatformChartNoteAttachmentDto[]>(chartId =>
    this.groupService.getChartNoteAttachments(chartId));
  readonly chartMeta = new HdKeyedCache<PlatformChartDto>(async chartId => (await this.library.getChartDocument(chartId)).meta);
  readonly cycleMoments = new HdKeyedCache<PlatformChartCycleMomentsDto>(key => {
    const { chartId, solarReturnYear } = parseCycleMomentsKey(key);
    return this.cyclesService.getCycleMoments(chartId, solarReturnYear);
  });

  private readonly remoteChangesSubject = new Subject<RemoteChartChange>();
  /** Seintjes van elders, na verwerking in de cache (voor de chart-pagina en de manager). */
  readonly remoteChanges$: Observable<RemoteChartChange> = this.remoteChangesSubject.asObservable();

  constructor() {
    inject(HdChartDataEvents).events$.pipe(takeUntilDestroyed()).subscribe(event => this.applyLocalEvent(event));
    setChartChangeListener({
      onRemoteChange: change => this.applyRemoteChange(change),
      onReconnected: () => this.markAllStale(),
    });
    inject(DestroyRef).onDestroy(() => setChartChangeListener(undefined));
  }

  /**
   * Reactief: laadt er voor deze chart iets zonder dat er al iets te tonen is? Bron voor de loader in
   * de tab; een stille herlaad (waarde al zichtbaar) telt bewust niet mee.
   */
  isColdLoading(chartId: string | null | undefined): boolean {
    if (!chartId) return false;
    if (isColdEntry(this.relations.entry(chartId))
      || isColdEntry(this.noteAttachments.entry(chartId))
      || isColdEntry(this.chartMeta.entry(chartId))) {
      return true;
    }
    const prefix = `${chartId}|`;
    for (const [key, entry] of this.cycleMoments.all()) {
      if (key.startsWith(prefix) && isColdEntry(entry)) return true;
    }
    return false;
  }

  private applyLocalEvent(event: HdChartDataEvent): void {
    switch (event.type) {
      case 'documentFetched':
      case 'notesUpdated':
        if (event.meta.id) this.chartMeta.set(event.meta.id, event.meta);
        break;
      case 'documentChanged':
      case 'chartsDeleted':
        this.invalidateDocuments(event.chartIds);
        break;
      case 'chartsRestored':
        // Kan weer opduiken in lijsten die hem nu niet bevatten: daar is geen gericht predicaat voor.
        this.invalidateDocuments(event.chartIds);
        this.relations.markAllStale();
        break;
      case 'relationCreated': {
        // De overkant toont het inverse type (docent ↔ student): dat bepaalt de server.
        const ids = new Set(event.chartIds);
        this.relations.invalidate(key => ids.has(key));
        break;
      }
      case 'relationNoteUpdated':
        this.patchRelationLists(list => list.map(relation =>
          relation.id === event.relationId ? { ...relation, note: event.note } : relation));
        break;
      case 'relationDeleted':
        this.patchRelationLists(list => list.filter(relation => relation.id !== event.relationId));
        break;
      case 'attachmentAdded': {
        const anchor = event.anchor;
        if ('chartId' in anchor) {
          this.noteAttachments.update(anchor.chartId, list => [...list, event.attachment]);
        } else {
          this.patchRelationLists(list => list.map(relation =>
            relation.id === anchor.chartRelationId
              ? { ...relation, noteAttachments: [...(relation.noteAttachments ?? []), event.attachment] }
              : relation));
        }
        break;
      }
      case 'attachmentDeleted':
        for (const [key, entry] of this.noteAttachments.all()) {
          if (entry.value?.some(attachment => attachment.id === event.attachmentId)) {
            this.noteAttachments.update(key, list => list.filter(attachment => attachment.id !== event.attachmentId));
          }
        }
        this.patchRelationLists(list => list.map(relation =>
          relation.noteAttachments?.some(attachment => attachment.id === event.attachmentId)
            ? { ...relation, noteAttachments: relation.noteAttachments.filter(attachment => attachment.id !== event.attachmentId) }
            : relation));
        break;
    }
  }

  private applyRemoteChange(change: RemoteChartChange): void {
    const ids = new Set(change.chartIds);
    for (const kind of change.kinds) {
      switch (kind) {
        case 'relations':
          this.relations.invalidate(key => ids.has(key));
          break;
        case 'notes':
          this.chartMeta.invalidate(key => ids.has(key));
          this.noteAttachments.invalidate(key => ids.has(key));
          break;
        case 'document':
        case 'deleted':
          this.invalidateDocuments(change.chartIds);
          break;
        case 'created':
          break;
      }
    }
    this.remoteChangesSubject.next(change);
  }

  /**
   * Naam, geboortegegevens of bestaan van een chart veranderde: zijn meta en cycle-momenten, plus de
   * relatielijsten van andere charts die hem als tegenpartij tonen (naam, type, icoon reizen mee op
   * de relatie-DTO).
   */
  private invalidateDocuments(chartIds: readonly string[]): void {
    const ids = new Set(chartIds);
    this.chartMeta.invalidate(key => ids.has(key));
    this.cycleMoments.invalidate(key => ids.has(parseCycleMomentsKey(key).chartId));
    this.relations.invalidate((_, list) => (list ?? []).some(relation => !!relation.otherChartId && ids.has(relation.otherChartId)));
  }

  private markAllStale(): void {
    this.relations.markAllStale();
    this.noteAttachments.markAllStale();
    this.chartMeta.markAllStale();
    this.cycleMoments.markAllStale();
  }

  /** Past `fn` toe op elke gecachte relatielijst en patcht enkel de lijsten die echt wijzigden. */
  private patchRelationLists(fn: (list: PlatformChartRelationDto[]) => PlatformChartRelationDto[]): void {
    for (const [key, entry] of this.relations.all()) {
      const list = entry.value;
      if (!list) continue;
      const next = fn(list);
      const changed = next.length !== list.length || next.some((relation, index) => relation !== list[index]);
      if (changed) this.relations.set(key, next);
    }
  }
}
